'use client';

import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Trash2, 
  ArrowUp, 
  ArrowDown, 
  Eye, 
  Code, 
  Send, 
  Settings, 
  Copy, 
  FileText, 
  X,
  Link as LinkIcon,
  Globe,
  RefreshCw,
  Loader2,
  Mic,
  ChevronDown
} from 'lucide-react';

// --- TYPES ---
type Story = {
  id: string;
  url: string;
  title: string;
  image?: string;
  category?: string;
  dek?: string;
};

type BucketKey = 'top_stories' | 'featured' | 'trending' | 'editors_pick';

type BucketConfig = {
  id: BucketKey;
  label: string;
  limit: number;
  description: string;
  fields: ('image' | 'category' | 'dek')[];
};

const BUCKETS: BucketConfig[] = [
  { id: 'top_stories', label: 'Top Stories', limit: 3, description: 'Large Image + Category (City, National, Crime)', fields: ['image', 'category'] },
  { id: 'featured', label: 'Featured (Podcast)', limit: 1, description: 'Podcast Episode (Slot 1) + Static Member Ad (Slot 2)', fields: ['dek'] },
  { id: 'trending', label: 'Top Trending', limit: 6, description: 'Simple link list', fields: [] },
  { id: 'editors_pick', label: "Editor's Choice", limit: 1, description: 'Title + Long Body Text', fields: ['dek'] },
];

// --- HTML GENERATOR ---
const generateHtml = (slots: Record<BucketKey, (Story | null)[]>, subject: string) => {
  const topStories = slots.top_stories.filter(Boolean) as Story[];
  const featured = slots.featured.filter(Boolean) as Story[];
  const trending = slots.trending.filter(Boolean) as Story[];
  const editor = slots.editors_pick[0] as Story | undefined;

  const renderTopStories = () => topStories.map(s => `
    <tr>
      <td style="padding: 15px 0; border-bottom: 1px solid #cccccc;">
        <!--[if mso]>
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"><tr>
          <td width="220" valign="top" style="padding-right:15px;">
            <a href='{{ TrackLink "${s.url}" }}'><img src="${s.image || ''}" width="220" style="display:block; width:220px; height:auto; border:0;" /></a>
          </td>
          <td width="430" valign="top">
            <div style="font-family: Georgia, Times, 'Times New Roman', serif; font-size:15px; color:#eb512e; text-transform:uppercase; margin-bottom:8px;">${s.category || 'NEWS'}</div>
            <div style="font-family: Georgia, Times, 'Times New Roman', serif; font-size:22px; font-weight:700; line-height:1.15;">
              <a href='{{ TrackLink "${s.url}" }}' style="color:#000000; text-decoration:none;">${s.title}</a>
            </div>
          </td>
        </tr></table>
        <![endif]-->
        <!--[if !mso]><!-- -->
        <div style="font-size:0; line-height:0;">
          <div class="m-imgcol" style="display:inline-block; vertical-align:top; width:100%; max-width:220px;">
            <div class="m-imgpad" style="padding-right:15px;">
              <a href='{{ TrackLink "${s.url}" }}'><img class="m-img" src="${s.image || ''}" width="220" style="display:block; width:100%; max-width:220px; height:auto;" /></a>
            </div>
          </div>
          <div class="m-textcol" style="display:inline-block; vertical-align:top; width:100%; max-width:430px;">
            <div class="m-gap" style="display:none;">&nbsp;</div>
            <div style="font-family: Georgia, Times, 'Times New Roman', serif; font-size:15px; color:#eb512e; text-transform:uppercase; margin-bottom:8px; line-height:1.2;">${s.category || 'NEWS'}</div>
            <div style="font-family: Georgia, Times, 'Times New Roman', serif; font-size:22px; font-weight:700; line-height:1.15;">
              <a href='{{ TrackLink "${s.url}" }}' style="color:#000000; text-decoration:none;">${s.title}</a>
            </div>
          </div>
        </div>
        <!--<![endif]-->
      </td>
    </tr>
  `).join('');

  const renderFeatured = () => {
    // Dynamic Podcast Slot (Slot 1)
    const podcast = featured[0];
    const podcastHtml = podcast ? `
      <tr>
        <td class="m-pad" style="padding: 0 0 15px 0; border-bottom: 1px solid #dadada;">
          <div style="font-family: Georgia, Times, 'Times New Roman', serif; font-size:16px; font-weight:700; line-height:1.2; margin-bottom:10px;">
            <a href='{{ TrackLink "${podcast.url}" }}' style="color:#000000; text-decoration:none;">
              ${podcast.title}
            </a>
          </div>
          <div style="font-family: Georgia, Times, 'Times New Roman', serif; font-size:14px; line-height:20px; color:#666666;">
            ${podcast.dek || ''}
          </div>
        </td>
      </tr>
    ` : '';

    // Static Support Block (Slot 2)
    const staticHtml = `
      <tr>
        <td class="m-pad" style="padding: 15px 0 0 0;">
          <div style="font-family: Georgia, Times, 'Times New Roman', serif; font-size:16px; font-weight:700; line-height:1.2; margin-bottom:10px;">
            <a href='{{ TrackLink "https://the-dallas-express.revv.co/become-a-dallas-express-member" }}' style="color:#000000; text-decoration:none;">
              Support Local Journalism: Become a Dallas Express Member
            </a>
          </div>
          <div style="font-family: Georgia, Times, 'Times New Roman', serif; font-size:14px; line-height:20px; color:#666666;">
            Become a Dallas Express member to support fact-based, paywall-free journalism in North Texas and gain exclusive access to member perks and newsroom insights.
          </div>
        </td>
      </tr>
    `;

    return podcastHtml + staticHtml;
  };

  const renderTrending = () => trending.map((s, i) => `
    <tr>
      <td class="m-pad" style="font-family: Georgia, Times, 'Times New Roman', serif; font-size:16px; font-weight:700; line-height:1.3; padding: ${i === 0 ? '6px 0' : '12px 0'}; border-bottom:${i === trending.length - 1 ? '0' : '1px solid #dadada'};">
        <a href='{{ TrackLink "${s.url}" }}' style="color:#000000; text-decoration:none; font-weight:700;">${s.title}</a>
      </td>
    </tr>
  `).join('');

  const renderEditor = () => editor ? `
    <tr>
      <td class="m-pad" style="padding: 0 0 4px 0;">
        <div style="font-family: Georgia, Times, 'Times New Roman', serif; font-size:16px; font-weight:700; line-height:1.2; margin-bottom:12px;">
          <a href='{{ TrackLink "${editor.url}" }}' style="color:#000000; text-decoration:none;">${editor.title}</a>
        </div>
        <div style="font-family: Georgia, Times, 'Times New Roman', serif; font-size:14px; line-height:22px; color:#111111;">
          ${(editor.dek || '').replace(/\n/g, '<br>')}
        </div>
      </td>
    </tr>
  ` : '';

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style type="text/css">
    body, table, td, a { -webkit-text-size-adjust:100%; -ms-text-size-adjust:100%; }
    table, td { mso-table-lspace:0pt; mso-table-rspace:0pt; }
    img { -ms-interpolation-mode:bicubic; border:0; display:block; }
    table { border-collapse:collapse !important; }
    body { margin:0 !important; padding:0 !important; width:100% !important; }
    @media only screen and (max-width: 620px) {
      .container { width:100% !important; max-width:100% !important; }
      .m-imgcol, .m-textcol { max-width:100% !important; width:100% !important; }
      .m-imgpad { padding-right:0 !important; }
      .m-img { max-width:100% !important; width:100% !important; height:auto !important; }
      .m-gap { display:block !important; height:12px !important; }
    }
  </style>
</head>
<body style="margin:0; padding:0;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" align="center">
    <tr>
      <td align="center">
        <table role="presentation" class="container" width="650" cellspacing="0" cellpadding="0" border="0" style="max-width:650px; width:650px;">
          <!-- TOP STORIES HEADER -->
          <tr><td style="padding:0 0 5px 0;"><span style="font-family: Georgia, Times, 'Times New Roman', serif; font-size:24px; font-weight:700; line-height:28px; color:#000000;">Top Stories</span></td></tr>
          <tr><td style="border-top:1px solid #000000; font-size:0; line-height:0; padding-bottom:15px;">&nbsp;</td></tr>
          ${renderTopStories()}
          
          <!-- FEATURED HEADER -->
          <tr><td class="m-pad" style="padding: 25px 0 6px 0;"><div style="font-family: Georgia, Times, 'Times New Roman', serif; font-size:24px; font-weight:700; line-height:28px; color:#000000;">Featured</div></td></tr>
          <tr><td class="m-pad" style="border-top:1px solid #000000; font-size:0; line-height:0; padding-bottom:15px;">&nbsp;</td></tr>
          ${renderFeatured()}
          
          <!-- TRENDING HEADER -->
          <tr><td class="m-pad" style="padding: 25px 0 6px 0;"><div style="font-family: Georgia, Times, 'Times New Roman', serif; font-size:24px; font-weight:700; line-height:28px; color:#000000;">Top Trending</div></td></tr>
          <tr><td class="m-pad" style="border-top:1px solid #000000; font-size:0; line-height:0; padding-bottom:15px;">&nbsp;</td></tr>
          ${renderTrending()}
          
          <!-- EDITORS PICK HEADER -->
          <tr><td class="m-pad" style="padding: 25px 0 6px 0;"><div style="font-family: Georgia, Times, 'Times New Roman', serif; font-size:24px; font-weight:700; line-height:28px; color:#000000;">Editor's Choice</div></td></tr>
          <tr><td class="m-pad" style="border-top:1px solid #000000; font-size:0; line-height:0; padding-bottom:15px;">&nbsp;</td></tr>
          ${renderEditor()}
          
          <!-- TRACKING -->
          <tr><td style="font-size:0; line-height:0;">{{ TrackView }}</td></tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
};

// --- HELPER TO DECODE HTML ENTITIES ---
const decodeHtml = (html: string) => {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
};

// --- MAIN APP COMPONENT ---
export default function NewsletterApp() {
  const [view, setView] = useState<'editor' | 'preview'>('editor');
  const [showSettings, setShowSettings] = useState(false);
  
  // Newsletter Info
  const [issueDate, setIssueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });

  const [campaignName, setCampaignName] = useState('');
  const [subject, setSubject] = useState('');
  const [apiInfo, setApiInfo] = useState({ url: '', username: '', password: '', listId: 1 });
  
  const [stories, setStories] = useState<Story[]>([]);
  // Initialize 'featured' with only 1 slot for Podcast
  const [slots, setSlots] = useState<Record<BucketKey, (Story | null)[]>>({
    top_stories: [null, null, null],
    featured: [null], 
    trending: [null, null, null, null, null, null],
    editors_pick: [null],
  });

  useEffect(() => {
    const savedApi = localStorage.getItem('dx_api_info');
    if (savedApi) setApiInfo(JSON.parse(savedApi));
  }, []);

  useEffect(() => {
    const [y, m, d] = issueDate.split('-');
    setCampaignName(`${m}.${d}.${y.slice(2)} Newsletter`);
  }, [issueDate]);

  useEffect(() => {
    const firstStory = slots.top_stories[0];
    if (firstStory) {
      setSubject(firstStory.title);
    }
  }, [slots.top_stories]);

  const saveSettings = () => {
    localStorage.setItem('dx_api_info', JSON.stringify(apiInfo));
    setShowSettings(false);
  };

  const addStory = (story: Story) => {
    setStories(prev => {
      if (prev.some(s => s.url === story.url)) return prev;
      return [story, ...prev];
    });
  };

  const deleteStory = (id: string) => {
    setStories(prev => prev.filter(s => s.id !== id));
    setSlots(prev => {
      const next = { ...prev };
      (Object.keys(next) as BucketKey[]).forEach(key => {
        next[key] = next[key].map(s => (s?.id === id ? null : s));
      });
      return next;
    });
  };

  const assignToSlot = (story: Story, bucket: BucketKey) => {
    setSlots(prev => {
      const next = { ...prev };
      const currentList = [...next[bucket]];
      
      if (currentList.some(s => s?.id === story.id)) return prev;

      const emptyIdx = currentList.findIndex(s => s === null);
      if (emptyIdx !== -1) {
        currentList[emptyIdx] = story;
      } else {
        alert(`${BUCKETS.find(b => b.id === bucket)?.label} is full! Clear a slot first.`);
        return prev;
      }
      
      next[bucket] = currentList;
      return next;
    });
  };

  const removeFromSlot = (bucket: BucketKey, index: number) => {
    setSlots(prev => {
      const next = { ...prev };
      next[bucket] = [...next[bucket]];
      next[bucket][index] = null;
      return next;
    });
  };

  const moveSlot = (bucket: BucketKey, from: number, direction: 'up' | 'down') => {
    setSlots(prev => {
      const next = { ...prev };
      const list = [...next[bucket]];
      const to = direction === 'up' ? from - 1 : from + 1;
      
      if (to < 0 || to >= list.length) return prev;
      
      const temp = list[from];
      list[from] = list[to];
      list[to] = temp;
      
      next[bucket] = list;
      return next;
    });
  };

  const pushToListmonk = async () => {
    if (!apiInfo.url || !apiInfo.username) {
      alert("Please configure Listmonk settings first.");
      setShowSettings(true);
      return;
    }

    const html = generateHtml(slots, subject);
    const auth = btoa(`${apiInfo.username}:${apiInfo.password}`);

    try {
      const res = await fetch(`${apiInfo.url}/api/campaigns`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${auth}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: campaignName,
          subject: subject,
          body: html,
          type: 'regular',
          content_type: 'html',
          lists: [Number(apiInfo.listId)],
          tags: ['newsletter']
        })
      });

      if (!res.ok) {
        const err = await res.text();
        throw new Error(err);
      }

      const data = await res.json();
      alert(`Success! Created "${campaignName}" (ID: ${data.data.id}). Check Listmonk drafts.`);
    } catch (e: any) {
      alert(`Error pushing to Listmonk: ${e.message}`);
    }
  };

  const copyToClipboard = () => {
    const html = generateHtml(slots, subject);
    
    // Create a temporary textarea element
    const textArea = document.createElement("textarea");
    textArea.value = html;
    
    // Make it hidden but part of the DOM
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    textArea.style.top = "0";
    document.body.appendChild(textArea);
    
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      const msg = successful ? 'HTML copied to clipboard!' : 'Failed to copy HTML';
      alert(msg);
    } catch (err) {
      console.error('Fallback: Oops, unable to copy', err);
      alert('Failed to copy HTML manually.');
    }
    
    document.body.removeChild(textArea);
  };

  return (
    <div className="min-h-screen bg-zinc-100 text-zinc-900 font-sans">
      
      <header className="bg-zinc-900 text-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-orange-600 rounded-lg flex items-center justify-center font-bold text-lg">DX</div>
            <h1 className="font-bold text-lg hidden sm:block">Newsletter Studio</h1>
          </div>
          
          <div className="flex items-center gap-3">
            <button onClick={() => setView(view === 'editor' ? 'preview' : 'editor')} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-800 hover:bg-zinc-700 text-sm font-medium transition-colors">
              {view === 'editor' ? <Eye size={16}/> : <Code size={16}/>}
              <span className="hidden sm:inline">{view === 'editor' ? 'Preview HTML' : 'Edit Content'}</span>
            </button>
            <button onClick={pushToListmonk} className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-600 hover:bg-orange-700 text-sm font-bold shadow-lg transition-transform active:scale-95">
              <Send size={16}/>
              <span className="hidden sm:inline">Push to Listmonk</span>
            </button>
            <button onClick={() => setShowSettings(true)} className="p-2 rounded-full hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors">
              <Settings size={20}/>
            </button>
          </div>
        </div>
      </header>

      {showSettings && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2"><Settings size={20}/> Listmonk Settings</h2>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-zinc-500 uppercase">Listmonk URL</label>
                <input value={apiInfo.url} onChange={e => setApiInfo({...apiInfo, url: e.target.value})} placeholder="https://list.dallasexpress.com" className="w-full border rounded-lg p-2 text-sm mt-1 focus:ring-2 focus:ring-orange-500 outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-zinc-500 uppercase">Username</label>
                  <input value={apiInfo.username} onChange={e => setApiInfo({...apiInfo, username: e.target.value})} className="w-full border rounded-lg p-2 text-sm mt-1 focus:ring-2 focus:ring-orange-500 outline-none" />
                </div>
                <div>
                  <label className="text-xs font-bold text-zinc-500 uppercase">Password</label>
                  <input type="password" value={apiInfo.password} onChange={e => setApiInfo({...apiInfo, password: e.target.value})} className="w-full border rounded-lg p-2 text-sm mt-1 focus:ring-2 focus:ring-orange-500 outline-none" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-zinc-500 uppercase">List ID (Default: 1)</label>
                <input type="number" value={apiInfo.listId} onChange={e => setApiInfo({...apiInfo, listId: Number(e.target.value)})} className="w-full border rounded-lg p-2 text-sm mt-1 focus:ring-2 focus:ring-orange-500 outline-none" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowSettings(false)} className="px-4 py-2 text-sm text-zinc-600 hover:bg-zinc-100 rounded-lg">Cancel</button>
              <button onClick={saveSettings} className="px-4 py-2 text-sm bg-zinc-900 text-white rounded-lg hover:bg-black font-medium">Save Settings</button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto p-4 sm:p-6">
        {view === 'preview' ? (
          <div className="space-y-4">
            <div className="flex justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-zinc-200">
              <div className="text-sm text-zinc-500">
                Subject: <span className="font-bold text-zinc-900">{subject}</span>
              </div>
              <button onClick={copyToClipboard} className="text-sm font-medium text-orange-600 flex items-center gap-1 hover:underline">
                <Copy size={16}/> Copy HTML
              </button>
            </div>
            <div className="bg-white shadow-2xl rounded-xl overflow-hidden border border-zinc-200">
              <iframe className="w-full h-[800px] bg-white" srcDoc={generateHtml(slots, subject)} title="Preview" />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-4 space-y-6">
              <div className="bg-white p-4 rounded-xl border border-zinc-200 shadow-sm space-y-3">
                <h3 className="font-bold text-zinc-900 text-sm uppercase tracking-wider">Campaign Info</h3>
                <div className="grid grid-cols-1 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-zinc-500">Scheduled Date</label>
                    <input 
                      type="date" 
                      value={issueDate} 
                      onChange={e => setIssueDate(e.target.value)} 
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-zinc-500">Campaign Name (Internal)</label>
                    <input 
                      type="text" 
                      value={campaignName} 
                      onChange={e => setCampaignName(e.target.value)} 
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-zinc-500">Subject Line (Auto-updates)</label>
                    <input 
                      type="text" 
                      value={subject} 
                      onChange={e => setSubject(e.target.value)} 
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:border-orange-500"
                      placeholder="Will auto-fill from top story..."
                    />
                  </div>
                </div>
              </div>

              {/* UNIFIED STORY MANAGER */}
              <StoryManager 
                stories={stories}
                onAdd={addStory}
                onAssign={assignToSlot}
                onDelete={deleteStory}
              />
            </div>

            <div className="lg:col-span-8 space-y-8">
              {BUCKETS.map(bucket => (
                <BucketSection 
                  key={bucket.id}
                  config={bucket}
                  slots={slots[bucket.id]}
                  onClear={(idx) => removeFromSlot(bucket.id, idx)}
                  onMoveUp={(idx) => moveSlot(bucket.id, idx, 'up')}
                  onMoveDown={(idx) => moveSlot(bucket.id, idx, 'down')}
                  onAssign={(story) => assignToSlot(story, bucket.id)}
                />
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// --- NEW COMPONENT: PODCAST DROPDOWN ---
function PodcastDropdown({ onSelect }: { onSelect: (s: Story) => void }) {
  const [episodes, setEpisodes] = useState<Story[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch podcasts on mount
  useEffect(() => {
    const fetchPods = async () => {
      setLoading(true);
      try {
        // Use a CORS proxy to fetch the RSS feed
        const rssUrl = 'https://feeds.transistor.fm/sarahzubiatebennett';
        const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(rssUrl)}`;
        
        const res = await fetch(proxyUrl);
        if (!res.ok) throw new Error('RSS Fetch failed');
        const text = await res.text();
        
        // Parse XML
        const parser = new DOMParser();
        const xml = parser.parseFromString(text, "text/xml");
        const items = Array.from(xml.querySelectorAll("item"));

        const stories = items.map((item) => {
          const title = item.querySelector("title")?.textContent || "Untitled Episode";
          const link = item.querySelector("link")?.textContent || "#";
          
          // Get description and strip HTML tags
          const description = item.querySelector("description")?.textContent || "";
          const doc = new DOMParser().parseFromString(description, 'text/html');
          const cleanDek = doc.body.textContent || "";

          return {
            id: item.querySelector("guid")?.textContent || crypto.randomUUID(),
            url: link,
            title: title,
            dek: cleanDek.substring(0, 300) + (cleanDek.length > 300 ? "..." : ""),
            category: 'PODCAST'
          };
        });
          
        if (stories.length === 0) {
          setError('No episodes found in feed.');
        }
        
        setEpisodes(stories.slice(0, 5));
      } catch (e: any) {
        console.error(e);
        setError('Failed to load RSS feed (CORS/Network).');
      } finally {
        setLoading(false);
      }
    };
    fetchPods();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    if (!id) return;
    const story = episodes.find(ep => ep.id === id);
    if (story) onSelect(story);
  };

  if (loading) return <div className="text-xs text-zinc-400 flex items-center gap-2"><Loader2 size={12} className="animate-spin"/> Loading episodes...</div>;
  if (error) return <div className="text-xs text-red-500">{error}</div>;

  return (
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
        <Mic size={14} className="text-blue-500" />
      </div>
      <select 
        onChange={handleChange}
        className="w-full bg-blue-50 border border-blue-200 text-blue-900 text-sm rounded-lg pl-8 pr-8 py-2 appearance-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
        defaultValue=""
      >
        <option value="" disabled>Select a Podcast Episode...</option>
        {episodes.map(ep => (
          <option key={ep.id} value={ep.id}>{ep.title}</option>
        ))}
      </select>
      <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
        <ChevronDown size={14} className="text-blue-400" />
      </div>
    </div>
  );
}

// --- STORY MANAGER COMPONENT ---
function StoryManager({ stories, onAdd, onAssign, onDelete }: { 
  stories: Story[], 
  onAdd: (s: Story) => void,
  onAssign: (s: Story, b: BucketKey) => void,
  onDelete: (id: string) => void
}) {
  const [activeTab, setActiveTab] = useState<'fetch' | 'manual'>('fetch');

  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm flex flex-col h-[700px]">
      {/* Tabs */}
      <div className="flex border-b border-zinc-200 shrink-0">
        <button 
          onClick={() => setActiveTab('fetch')}
          className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 ${activeTab === 'fetch' ? 'bg-orange-50 text-orange-700 border-b-2 border-orange-500' : 'text-zinc-500 hover:bg-zinc-50'}`}
        >
          <Globe size={16}/> Fetch
        </button>
        <button 
          onClick={() => setActiveTab('manual')}
          className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 ${activeTab === 'manual' ? 'bg-orange-50 text-orange-700 border-b-2 border-orange-500' : 'text-zinc-500 hover:bg-zinc-50'}`}
        >
          <LinkIcon size={16}/> Manual
        </button>
      </div>

      {/* Input Area */}
      <div className="p-4 bg-zinc-50 border-b border-zinc-200 shrink-0">
        {activeTab === 'fetch' ? <FetcherForm onAdd={onAdd} /> : <ManualForm onAdd={onAdd} />}
      </div>

      {/* List Area */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2 bg-white">
        {stories.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-zinc-400 text-sm italic opacity-60">
             <div className="mb-2"><FileText size={24} /></div>
             <div>Stories you add will appear here</div>
          </div>
        ) : (
           stories.map(story => (
             <StoryCard 
               key={story.id} 
               story={story} 
               onAssign={(bucket) => onAssign(story, bucket)} 
               onDelete={() => onDelete(story.id)}
             />
           ))
        )}
      </div>
    </div>
  );
}

// --- WP FETCHER FORM ---
function FetcherForm({ onAdd }: { onAdd: (s: Story) => void }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchStories = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('https://dallasexpress.com/wp-json/wp/v2/posts?per_page=30&_embed');
      if (!res.ok) throw new Error('Failed to fetch from DallasExpress.com');
      const posts = await res.json();

      [...posts].reverse().forEach((post: any) => {
        const title = decodeHtml(post.title?.rendered || '');
        const url = post.link;
        const rawDek = post.excerpt?.rendered || '';
        const dek = decodeHtml(rawDek.replace(/<[^>]+>/g, '')).trim();
        const image = post._embedded?.['wp:featuredmedia']?.[0]?.source_url || '';
        const cats = post._embedded?.['wp:term']?.[0];
        const categoryName = cats && cats.length > 0 ? cats[0].name : '';
        const category = categoryName === 'Uncategorized' ? 'NEWS' : categoryName.toUpperCase();

        onAdd({
          id: String(post.id),
          url,
          title,
          image,
          category,
          dek
        });
      });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="text-xs text-zinc-500">
        Pulls the latest 30 headlines directly from dallasexpress.com API.
      </div>
      {error && <div className="text-xs text-red-600 bg-red-50 p-2 rounded">{error}</div>}
      
      <button 
        onClick={fetchStories} 
        disabled={loading}
        className="w-full bg-zinc-900 text-white font-medium py-2.5 rounded-lg text-sm hover:bg-black active:scale-[0.99] transition-all flex items-center justify-center gap-2 disabled:opacity-70"
      >
        {loading ? <Loader2 size={16} className="animate-spin"/> : <RefreshCw size={16}/>}
        {loading ? 'Fetching...' : 'Load Latest Stories'}
      </button>
    </div>
  );
}

// --- MANUAL ENTRY FORM ---
function ManualForm({ onAdd }: { onAdd: (s: Story) => void }) {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [image, setImage] = useState('');
  const [category, setCategory] = useState('');
  const [dek, setDek] = useState('');

  const handleSubmit = () => {
    if (!title) return;
    onAdd({
      id: crypto.randomUUID(),
      url: url || '#',
      title,
      image,
      category,
      dek
    });
    setUrl('');
    setTitle('');
    setImage('');
    setDek('');
  };

  return (
    <div className="space-y-3 animate-in slide-in-from-top-1">
      <input 
        placeholder="Article URL" 
        value={url}
        onChange={e => setUrl(e.target.value)}
        className="w-full bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:border-orange-500 outline-none"
      />
      <input 
        placeholder="Headline" 
        value={title}
        onChange={e => setTitle(e.target.value)}
        className="w-full bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 text-sm font-semibold focus:border-orange-500 outline-none"
      />
      <div className="grid grid-cols-2 gap-2">
        <select 
          value={category}
          onChange={e => setCategory(e.target.value)}
          className="bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 text-xs focus:border-orange-500 outline-none"
        >
          <option value="">(No Tag)</option>
          <option value="CITY">CITY</option>
          <option value="NATIONAL">NATIONAL</option>
          <option value="CRIME">CRIME</option>
          <option value="BUSINESS">BUSINESS</option>
          <option value="POLITICS">POLITICS</option>
          <option value="PODCAST">PODCAST</option>
        </select>
         <input 
          placeholder="Image URL" 
          value={image}
          onChange={e => setImage(e.target.value)}
          className="bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 text-xs focus:border-orange-500 outline-none"
        />
      </div>
      <textarea 
        placeholder="Summary / Dek" 
        value={dek}
        onChange={e => setDek(e.target.value)}
        className="w-full bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:border-orange-500 outline-none h-20 resize-none"
      />
      <button 
        onClick={handleSubmit}
        className="w-full bg-white border border-zinc-300 text-zinc-900 font-medium py-2 rounded-lg text-sm hover:bg-zinc-50 active:scale-[0.99] transition-all flex items-center justify-center gap-2"
      >
        <Plus size={16}/> Add to Inbox
      </button>
    </div>
  );
}

function StoryCard({ story, onAssign, onDelete }: { story: Story, onAssign: (b: BucketKey) => void, onDelete: () => void }) {
  const [showAssign, setShowAssign] = useState(false);

  return (
    <div className="group relative bg-white border border-zinc-200 rounded-lg p-3 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
             <div className="text-xs font-bold text-orange-600 uppercase">{story.category}</div>
             {story.category === 'PODCAST' && <div className="text-[10px] font-bold bg-blue-100 text-blue-700 px-1.5 rounded">NEW EPISODE</div>}
          </div>
          <div className="text-sm font-semibold text-zinc-900 leading-tight line-clamp-2">{story.title}</div>
          <div className="text-xs text-zinc-400 mt-1 truncate">{story.url}</div>
        </div>
        <button onClick={onDelete} className="text-zinc-300 hover:text-red-500 p-1">
          <Trash2 size={14}/>
        </button>
      </div>
      
      {/* Action Bar */}
      <div className="mt-3 pt-2 border-t border-zinc-100 flex gap-2">
        <div className="relative flex-1">
           <button 
             onClick={() => setShowAssign(!showAssign)}
             className="w-full text-xs font-medium bg-zinc-100 hover:bg-zinc-200 text-zinc-700 py-1.5 rounded flex items-center justify-center gap-1"
           >
             Add to...
           </button>
           
           {showAssign && (
             <div className="absolute top-full left-0 w-full mt-1 bg-white border border-zinc-200 shadow-xl rounded-lg z-10 overflow-hidden text-left animate-in fade-in zoom-in duration-200">
               {BUCKETS.map(b => (
                 <button 
                   key={b.id}
                   onClick={() => { onAssign(b.id); setShowAssign(false); }}
                   className="block w-full text-left px-3 py-2 text-xs hover:bg-orange-50 text-zinc-700 hover:text-orange-700 border-b border-zinc-50 last:border-0"
                 >
                   {b.label}
                 </button>
               ))}
             </div>
           )}
        </div>
      </div>
    </div>
  );
}

function BucketSection({ config, slots, onClear, onMoveUp, onMoveDown, onAssign }: { 
  config: BucketConfig, 
  slots: (Story | null)[],
  onClear: (i: number) => void,
  onMoveUp: (i: number) => void,
  onMoveDown: (i: number) => void,
  onAssign: (s: Story) => void
}) {
  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden">
      <div className="bg-zinc-50 px-4 py-3 border-b border-zinc-200 flex justify-between items-center">
        <div>
          <h3 className="font-bold text-zinc-900 text-sm uppercase">{config.label}</h3>
          <p className="text-xs text-zinc-500">{config.description}</p>
        </div>
        <div className="text-xs font-bold bg-zinc-200 px-2 py-1 rounded text-zinc-600">
          {slots.filter(Boolean).length} / {config.limit}
        </div>
      </div>
      
      <div className="divide-y divide-zinc-100">
        {slots.map((slot, idx) => (
          <div key={idx} className="p-3 flex items-center gap-3 group bg-white hover:bg-zinc-50 transition-colors">
            {/* Index Indicator */}
            <div className="w-6 h-6 rounded-full bg-zinc-100 text-zinc-400 text-xs font-bold flex items-center justify-center shrink-0">
              {idx + 1}
            </div>

            {/* Content or Placeholder */}
            <div className="flex-1 min-w-0">
              {slot ? (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    {config.fields.includes('category') && slot.category && (
                      <span className="text-[10px] font-bold bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">{slot.category}</span>
                    )}
                    <span className="text-sm font-semibold text-zinc-900 truncate block">{slot.title}</span>
                  </div>
                  
                  {config.fields.includes('image') && (
                    <div className="flex items-center gap-2 text-xs text-zinc-400">
                       <div className={`w-2 h-2 rounded-full ${slot.image ? 'bg-green-500' : 'bg-red-300'}`}></div>
                       {slot.image ? 'Image Set' : 'Missing Image'}
                    </div>
                  )}

                  {config.fields.includes('dek') && (
                     <div className="text-xs text-zinc-500 truncate mt-0.5">{slot.dek || 'No summary text...'}</div>
                  )}
                </div>
              ) : (
                 // IMPLANTED DROPDOWN FOR FEATURED, ELSE STANDARD PLACEHOLDER
                 config.id === 'featured' ? (
                   <PodcastDropdown onSelect={onAssign} />
                 ) : (
                   <div className="text-sm text-zinc-300 italic">Empty Slot (Assign from inbox)</div>
                 )
              )}
            </div>

            {/* Controls */}
            {slot && (
              <div className="flex items-center gap-1 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => onMoveUp(idx)} disabled={idx === 0} className="p-1.5 hover:bg-white hover:shadow rounded text-zinc-500 hover:text-zinc-900 disabled:opacity-20">
                  <ArrowUp size={14}/>
                </button>
                <button onClick={() => onMoveDown(idx)} disabled={idx === slots.length - 1} className="p-1.5 hover:bg-white hover:shadow rounded text-zinc-500 hover:text-zinc-900 disabled:opacity-20">
                  <ArrowDown size={14}/>
                </button>
                <button onClick={() => onClear(idx)} className="p-1.5 hover:bg-red-50 hover:text-red-600 rounded text-zinc-400 ml-1">
                  <X size={14}/>
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
